"use client";

import { PortalStatusBadge } from "@/app/portal/_view/portal-status-badge";
import { useFormatDate } from "@/lib/i18n/format";
import { cn } from "@v1/ui/utils";
import { Mail, MessageSquareText } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ReactElement, useCallback } from "react";
import { MessengerAgentAvatar, MessengerTeamAvatar } from "./messenger-avatars";
import { useMessenger } from "./messenger-context";
import { MessengerTabHeader } from "./messenger-header";
import { MessengerLoadOlder, MessengerPrimaryButton } from "./messenger-thread-parts";
import { type MessengerConversation, useMessengerConversations } from "./use-messenger-conversations";

const PAGE_SIZE = 20;

/** Today → 14:30, earlier → 07.03. */
export function useMessengerShortTime(): (at: number) => string {
  const formatDate = useFormatDate();
  return useCallback(
    (at: number): string =>
      new Date(at).toDateString() === new Date().toDateString() ? formatDate.time(at) : formatDate.dayMonth(at),
    [formatDate],
  );
}

/**
 * One conversation. AI only: triangle on green tint. Team involved: people
 * icon on grey, plus status. Mail/WhatsApp tickets name their channel.
 */
export function MessengerConversationRow({
  row,
  compact = false,
}: {
  readonly row: MessengerConversation;
  readonly compact?: boolean;
}): ReactElement {
  const t = useTranslations("messenger.lists");
  const { nav } = useMessenger();
  const shortTime = useMessengerShortTime();
  return (
    <button
      type="button"
      onClick={() => nav.push(row.open)}
      className={cn(
        "flex w-full items-center gap-3 text-left transition-colors hover:bg-action-500/[0.04]",
        compact ? "px-4 pb-3 pt-2" : "min-h-16 px-4 py-2.5",
      )}
    >
      {row.team ? <MessengerTeamAvatar /> : <MessengerAgentAvatar size="md" />}
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex min-w-0 items-baseline gap-2">
          <span
            className={cn(
              "min-w-0 flex-1 truncate text-[13.5px] text-primary/90",
              row.unread ? "font-semibold" : "font-medium",
            )}
          >
            {row.title || t("neueNachricht")}
          </span>
          <span className="shrink-0 text-[11.5px] tabular-nums text-primary/40">{shortTime(row.at)}</span>
        </span>
        <span className="flex min-w-0 items-center gap-2">
          <span className="flex min-w-0 flex-1 items-center gap-1.5 text-[13px] text-primary/50">
            {row.status !== null && row.status !== "new" ? <PortalStatusBadge status={row.status} /> : null}
            {row.channel !== null ? (
              <span className="inline-flex shrink-0 items-center gap-1 text-[12px] text-primary/45">
                {row.channel === "email" ? (
                  <Mail className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                ) : (
                  <MessageSquareText className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
                )}
                {row.channel === "email" ? t("email") : t("whatsapp")}
              </span>
            ) : null}
            {row.preview ? <span className="min-w-0 truncate">{row.preview}</span> : null}
          </span>
          {row.unread ? <span aria-hidden className="h-2 w-2 shrink-0 rounded-full bg-action-500" /> : null}
        </span>
      </span>
    </button>
  );
}

/** „Nachrichten“: every conversation of the person in one list, newest first. */
export function MessengerConversationList(): ReactElement {
  const t = useTranslations("messenger");
  const { nav } = useMessenger();
  const { rows, loading, canLoadMore, loadMore } = useMessengerConversations(PAGE_SIZE);
  return (
    <>
      <MessengerTabHeader title={t("tabs.nachrichten")} />
      <div className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain pb-20">
        {!loading && rows.length === 0 ? (
          <p className="px-6 pt-16 text-center text-[13px] text-primary/45">{t("lists.keineNachrichten")}</p>
        ) : (
          <ul className="divide-y divide-border/40">
            {rows.map((row) => (
              <li key={row.key}>
                <MessengerConversationRow row={row} />
              </li>
            ))}
          </ul>
        )}
        {canLoadMore ? <MessengerLoadOlder onClick={loadMore} /> : null}
      </div>
      <div className="pointer-events-none absolute inset-x-0 bottom-[4.5rem] flex justify-center">
        <span className="pointer-events-auto">
          <MessengerPrimaryButton
            label={t("lists.neueNachricht")}
            onClick={() => nav.push({ kind: "chat", sessionId: null })}
          />
        </span>
      </div>
    </>
  );
}
